import React, { useContext } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AppContext from 'context/Context';
import {
  faUsers,
  faUserCheck,
  faFilter,
  faChevronDown,
  faHeadset,
  faCheck
} from '@fortawesome/free-solid-svg-icons';

const formatTitleCase = str => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getDisplayName = (fullName, fallbackUser) => {
  if (!fullName) return fallbackUser || 'Usuario';
  const clean = fullName.trim();
  if (!clean) return fallbackUser || 'Usuario';

  const parts = clean.split(/\s+/).filter(Boolean);
  const toTitleCase = word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

  if (parts.length === 1) {
    return toTitleCase(parts[0]);
  }
  const firstName = toTitleCase(parts[0]);
  const surname = parts.length >= 3 ? toTitleCase(parts[2]) : toTitleCase(parts[1]);
  return `${firstName} ${surname}`;
};

const AgentFilterSelect = ({
  selectedAgentId,
  onSelectAgent,
  agents = [],
  currentUserId,
  currentUserName,
  isLoadingAgents = false,
  variant = 'default'
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const isMine = currentUserId && String(selectedAgentId) === String(currentUserId);
  const isAll = !selectedAgentId || selectedAgentId === '' || selectedAgentId === 'todos';

  // Obtener el nombre del asesor seleccionado para el modo 'card'
  const getSelectedDisplayName = () => {
    if (isMine) {
      return getDisplayName(currentUserName, 'Mis métricas');
    }
    if (isAll) {
      return 'Todos los asesores';
    }
    const found = agents.find(a => String(a.idUsuario) === String(selectedAgentId));
    if (found) {
      return formatTitleCase(found.vNombreCompleto || found.vNombre) || `Asesor #${found.idUsuario}`;
    }
    return 'Seleccionar asesor';
  };

  const badgeIcon = isMine ? faUserCheck : isAll ? faUsers : faHeadset;
  const badgeBg = isDark
    ? (isMine ? 'rgba(16, 185, 129, 0.2)' : 'rgba(14, 165, 233, 0.2)')
    : (isMine ? '#ecfdf5' : '#e0f2fe');
  const badgeColor = isDark
    ? (isMine ? '#34d399' : '#38bdf8')
    : (isMine ? '#059669' : '#0284c7');
  const subLabel = isMine ? 'Mis Métricas' : isAll ? 'Vista General' : 'Asesor Asignado';

  const renderOptions = () => (
    <>
      {/* Opción por defecto: Mis métricas (usuario que inició sesión) */}
      {currentUserId && (
        <option value={String(currentUserId)}>
          {getDisplayName(currentUserName)}
        </option>
      )}

      {/* Opción para ver todos */}
      <option value="">
        Todos los asesores
      </option>

      {/* Lista de demás asesores */}
      {agents
        .filter(a => String(a.idUsuario) !== String(currentUserId))
        .map(agent => {
          const name = formatTitleCase(agent.vNombreCompleto || agent.vNombre) || `Asesor #${agent.idUsuario}`;
          return (
            <option key={agent.idUsuario} value={String(agent.idUsuario)}>
              {name}
            </option>
          );
        })}
    </>
  );

  if (variant === 'card') {
    return (
      <Dropdown className="w-100">
        <Dropdown.Toggle
          as="div"
          bsPrefix="se-agent-card-select"
          role="button"
          tabIndex={0}
        >
          <div className="d-flex align-items-center gap-3 overflow-hidden flex-grow-1">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
              style={{ width: '38px', height: '38px', backgroundColor: badgeBg, color: badgeColor }}
            >
              <FontAwesomeIcon icon={badgeIcon} style={{ fontSize: '1.05rem' }} />
            </div>
            <div className="overflow-hidden pe-2 text-start">
              <div
                className="text-uppercase fw-bold text-truncate"
                style={{
                  fontSize: '0.64rem',
                  letterSpacing: '0.04em',
                  color: isMine ? (isDark ? '#34d399' : '#059669') : (isDark ? '#94a3b8' : '#64748b')
                }}
              >
                {subLabel}
              </div>
              <div className="fw-bold text-900 text-truncate" style={{ fontSize: '0.90rem' }}>
                {getSelectedDisplayName()}
              </div>
            </div>
          </div>

          <FontAwesomeIcon
            icon={faChevronDown}
            className="flex-shrink-0 ms-2 text-500"
            style={{ fontSize: '0.82rem' }}
          />
        </Dropdown.Toggle>

        <Dropdown.Menu className="se-agent-dropdown-menu shadow-lg border">
          {/* Opción 1: Mis métricas (usuario logueado) */}
          {currentUserId && (
            <Dropdown.Item
              as="button"
              type="button"
              className={`se-agent-dropdown-item ${isMine ? 'se-item-selected-green' : ''}`}
              onClick={() => onSelectAgent(String(currentUserId))}
            >
              <div className="d-flex align-items-center gap-2 text-truncate">
                <FontAwesomeIcon icon={faUserCheck} style={{ color: isDark ? '#34d399' : '#059669', width: '16px' }} />
                <span className="text-truncate">
                  ({getDisplayName(currentUserName)})
                </span>
              </div>
              {isMine && <FontAwesomeIcon icon={faCheck} className="ms-2" style={{ color: isDark ? '#34d399' : '#059669' }} />}
            </Dropdown.Item>
          )}

          {/* Opción 2: Todos los asesores */}
          <Dropdown.Item
            as="button"
            type="button"
            className={`se-agent-dropdown-item ${isAll ? 'se-item-selected-blue' : ''}`}
            onClick={() => onSelectAgent('')}
          >
            <div className="d-flex align-items-center gap-2 text-truncate">
              <FontAwesomeIcon icon={faUsers} style={{ color: isDark ? '#38bdf8' : '#0284c7', width: '16px' }} />
              <span className="text-truncate">Todos los asesores (General)</span>
            </div>
            {isAll && <FontAwesomeIcon icon={faCheck} className="ms-2" style={{ color: isDark ? '#38bdf8' : '#0284c7' }} />}
          </Dropdown.Item>

          {/* Lista de demás asesores */}
          {agents.length > 0 && <Dropdown.Divider className="my-1" />}
          {agents.length > 0 && (
            <Dropdown.Header className="px-3 py-1 text-uppercase text-500" style={{ fontSize: '0.65rem', fontWeight: 700 }}>
              Equipo de Soporte
            </Dropdown.Header>
          )}

          {isLoadingAgents && agents.length === 0 ? (
            <div className="text-center py-2 text-500" style={{ fontSize: '0.80rem' }}>
              <Spinner animation="border" size="sm" className="me-2" />
              Cargando asesores...
            </div>
          ) : (
            agents
              .filter(a => String(a.idUsuario) !== String(currentUserId))
              .map(agent => {
                const agentId = String(agent.idUsuario);
                const isSelected = String(selectedAgentId) === agentId;
                const agentName = formatTitleCase(agent.vNombreCompleto || agent.vNombre) || `Asesor #${agent.idUsuario}`;
                return (
                  <Dropdown.Item
                    key={agent.idUsuario}
                    as="button"
                    type="button"
                    className={`se-agent-dropdown-item ${isSelected ? 'se-item-selected-blue' : ''}`}
                    onClick={() => onSelectAgent(agentId)}
                  >
                    <div className="d-flex align-items-center gap-2 text-truncate">
                      <FontAwesomeIcon icon={faHeadset} style={{ color: isSelected ? (isDark ? '#38bdf8' : '#0284c7') : (isDark ? '#94a3b8' : '#64748b'), width: '16px' }} />
                      <span className="text-truncate">{agentName}</span>
                    </div>
                    {isSelected && <FontAwesomeIcon icon={faCheck} className="ms-2" style={{ color: isDark ? '#38bdf8' : '#0284c7' }} />}
                  </Dropdown.Item>
                );
              })
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  return (
    <div className="d-flex align-items-center gap-2 se-agent-filter-container">
      <label
        htmlFor="tickets-agent-filter-select"
        className="d-flex align-items-center gap-1 text-600 mb-0 fw-semibold"
        style={{ fontSize: '0.80rem', whiteSpace: 'nowrap' }}
      >
        <FontAwesomeIcon
          icon={isMine ? faUserCheck : isAll ? faUsers : faFilter}
          style={{ color: isMine ? (isDark ? '#34d399' : '#059669') : (isDark ? '#38bdf8' : '#0886ca') }}
        />
        <span>Asesor:</span>
      </label>

      <select
        id="tickets-agent-filter-select"
        className="form-select form-select-sm border shadow-none text-900 fw-semibold se-agent-filter-select"
        style={{
          fontSize: '0.82rem',
          borderRadius: '8px',
          cursor: 'pointer'
        }}
        value={isAll ? '' : String(selectedAgentId)}
        onChange={e => onSelectAgent(e.target.value)}
        title="Filtrar métricas por asesor asignado"
        disabled={isLoadingAgents && agents.length === 0}
      >
        {renderOptions()}
      </select>
    </div>
  );
};

export default AgentFilterSelect;
